import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Switch,
  FormControlLabel,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import {
  Business,
  School,
  CalendarToday,
  Numbers,
  Notes,
  Add,
  Close,
  Chat,
  CheckBox,
  CheckBoxOutlineBlank,
} from "@mui/icons-material";

import NumericInput from "../../Components/NumericInput";
import CustomAutocomplete from "../../Components/CustomAutocomplete";
import CourseManagerDialog from "../../Components/CourseManagerDialog";

import HttpClient from "../../../../helpers/HttpClient";
import OverlayLoader from "../../../../components/OverlayLoader";
import { showToast } from "../../../../utils/toast";

interface CursoSeleccionado {
  code: string;
  name: string;
  chatEnabled: boolean;
  category?: string
}

interface Distribuidor {
  id: string;
  name: string;
}

export default function SerialGenerationPage() {
  const [openLoader, setOpenLoader] = useState(true);
  const [distribuidoresDisponibles, setDistribuidoresDisponibles] = useState(
    []
  );
  const [cursosDisponibles, setCursosDisponibles] = useState([]);
  const [distribuidorSeleccionado, setDistribuidorSeleccionado] =
    useState<Distribuidor | null>(null);
  const [cursosSeleccionados, setCursosSeleccionados] = useState<
    CursoSeleccionado[]
  >([]);
  const [numeroMeses, setNumeroMeses] = useState("12");
  const [numeroActivaciones, setNumeroActivaciones] = useState("1");
  const [cantidadSeriales, setCantidadSeriales] = useState("100");
  const [observaciones, setObservaciones] = useState("");
  const [openCursosDialog, setOpenCursosDialog] = useState(false);
  const [tempCursosSeleccionados, setTempCursosSeleccionados] = useState<
    CursoSeleccionado[]
  >([]);

  const reset = () => {
    setDistribuidorSeleccionado(null)
    setCursosSeleccionados([])
    setNumeroMeses("12");
    setNumeroActivaciones("1");
    setCantidadSeriales("100");
    setObservaciones("");
    setTempCursosSeleccionados([])
  }

  const getDistributors = async () => {
    const result = await HttpClient().get(`/admin/distributor`);
    if (result.error) {
      setOpenLoader(false);
      showToast(
        "No se pudo cargar información de distribuidores. Refresca la página e intenta nuevamente.",
        "error"
      );
      return;
    }

    setDistribuidoresDisponibles(result.data);
    setOpenLoader(false);
  };

  const getCourses = async () => {
    const result = await HttpClient().get(`/admin/courses`);
    if (result.error) {
      setOpenLoader(false);
      showToast(
        "No se pudo cargar información de cursos. Refresca la página e intenta nuevamente.",
        "error"
      );
      return;
    }

    setCursosDisponibles(result.data);
    setOpenLoader(false);
  };

  const createBatch = async (data) => {
    setOpenLoader(true);
    const result = await HttpClient().post(`/admin/batch/create`, data);
    console.log(result)
    if (result.error) {
      setOpenLoader(false);
      showToast(
        "No se pudo guardar el lote. Intenta nuevamente.",
        "error"
      );
      return;
    }
    setOpenLoader(false);
    if (result.status == 201){
      showToast(
        "El lote fue guardado correctamente.",
        "success"
      );
      reset();
    } else {
      showToast(
        "No se creó el lote. Intenta nuevamente. Si el error persiste contacta al soporte.",
        "warning"
      );
    }

  };

  const handleOpenCursosDialog = () => {
    setTempCursosSeleccionados([...cursosSeleccionados]);
    setOpenCursosDialog(true);
  };

  const handleCloseCursosDialog = () => {
    setOpenCursosDialog(false);
  };

  const handleSaveCursos = () => {
    setCursosSeleccionados([...tempCursosSeleccionados]);
    setOpenCursosDialog(false);
  };

  const handleAddCurso = (curso: { code: string; name: string }) => {
    if (!tempCursosSeleccionados.find((c) => c.code === curso.code)) {
      setTempCursosSeleccionados((prev) => [
        ...prev,
        { ...curso, chatEnabled: false },
      ]);
    }
  };

  const handleRemoveCurso = (cursoCode: string) => {
    setTempCursosSeleccionados((prev) =>
      prev.filter((c) => c.code !== cursoCode)
    );
  };

  const handleToggleChatInModal = (cursoCode: string) => {
    setTempCursosSeleccionados((prev) =>
      prev.map((curso) =>
        curso.code === cursoCode
          ? { ...curso, chatEnabled: !curso.chatEnabled }
          : curso
      )
    );
  };

  const handleToggleChatMasivo = (enabled: boolean) => {
    setTempCursosSeleccionados((prev) =>
      prev.map((curso) => ({ ...curso, chatEnabled: enabled }))
    );
  };

  const handleToggleChat = (cursoCode: string) => {
    setCursosSeleccionados((prev) =>
      prev.map((curso) =>
        curso.code === cursoCode
          ? { ...curso, chatEnabled: !curso.chatEnabled }
          : curso
      )
    );
  };

  const handleToggleChatMasivoMain = (enabled: boolean) => {
    setCursosSeleccionados((prev) =>
      prev.map((curso) => ({ ...curso, chatEnabled: enabled }))
    );
  };

  const handleGenerar = async() => {
    const meses = Number.parseInt(numeroMeses) || 0;
    const seriales = Number.parseInt(cantidadSeriales) || 0;

    if (meses < 1 || seriales < 1) {
      showToast(
        "Por favor, ingrese valores válidos para meses y cantidad de seriales", "error"
      );
      return;
    }

    await createBatch({
      distributor: distribuidorSeleccionado?.id,
      months: meses,
      amount: seriales,
      activations: numeroActivaciones,
      observations: observaciones,
      courses: [...new Set(cursosSeleccionados.map((ele) => ele.code))].join(","),
      selectedCoursesData: cursosSeleccionados.map(({ category, ...rest }) => rest)
    })

  };

  const cursosDisponiblesParaAgregar = cursosDisponibles.filter(
    (curso) => !tempCursosSeleccionados.find((c) => c.code === curso.code)
  );

  const isFormValid = () => {
    const meses = Number.parseInt(numeroMeses) || 0;
    const seriales = Number.parseInt(cantidadSeriales) || 0;
    return (
      cursosSeleccionados.length > 0 &&
      distribuidorSeleccionado !== null &&
      meses >= 1 &&
      seriales >= 1
    );
  };

  useEffect(() => {
    getDistributors();
    getCourses();
  }, []);

  return (
    <Box sx={{ backgroundColor: "#F5F5F5", minHeight: "100vh", p: 2 }}>
      {/* Header */}
      <OverlayLoader open={openLoader} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          color="#374151"
          sx={{ fontSize: "1.3rem" }}
        >
          Generación lotes seriales online
        </Typography>
        {/*<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            sx={{
              width: 50,
              height: 50,
              backgroundColor: "#1976D2",
              fontSize: "1.2rem",
              fontWeight: "bold",
            }}
          >
            G
          </Avatar>
          <Typography
            variant="caption"
            color="#6B7280"
            sx={{ fontSize: "0.7rem" }}
          >
            editores
          </Typography>
        </Box>*/}
      </Box>

      <Card
        sx={{
          borderRadius: 2,
          border: "1px solid #E5E7EB",
          backgroundColor: "white",
        }}
      >
        <CardContent sx={{ p: 2 }}>
          {/* Distribuidor */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
              >
                <Business sx={{ color: "#6B7280", fontSize: 16 }} />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ fontSize: "0.85rem" }}
                >
                  Distribuidor:
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <CustomAutocomplete 
                    options={distribuidoresDisponibles}
                    value={distribuidorSeleccionado}
                    onChange={(event, newValue) => {
                      setDistribuidorSeleccionado(newValue);
                    }}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                />
                <Avatar
                  sx={{ width: 28, height: 28, backgroundColor: "#6B7280" }}
                >
                  <Business sx={{ fontSize: 14 }} />
                </Avatar>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Cursos */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <School sx={{ color: "#6B7280", fontSize: 16 }} />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ fontSize: "0.85rem" }}
                >
                  Curso(s) Seleccionados: ({cursosSeleccionados.length})
                </Typography>
              </Box>
              {cursosSeleccionados.length > 0 && (
                <Box sx={{ display: "flex", gap: 0.8 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CheckBoxOutlineBlank sx={{ fontSize: 14 }} />}
                    onClick={() => handleToggleChatMasivoMain(false)}
                    sx={{ fontSize: "0.7rem", py: 0.3 }}
                  >
                    Desactivar Chat Todos
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CheckBox sx={{ fontSize: 14 }} />}
                    onClick={() => handleToggleChatMasivoMain(true)}
                    sx={{
                      fontSize: "0.7rem",
                      py: 0.3,
                      backgroundColor: "#10B981",
                      "&:hover": { backgroundColor: "#059669" },
                    }}
                  >
                    Activar Chat Todos
                  </Button>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: 1.5,
                p: 1,
                backgroundColor: "#F9FAFB",
                minHeight: 80,
                maxHeight: 200,
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#F3F4F6",
                  borderRadius: "2px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#D1D5DB",
                  borderRadius: "2px",
                  "&:hover": {
                    backgroundColor: "#9CA3AF",
                  },
                },
              }}
            >
              {cursosSeleccionados.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 80,
                    color: "#9CA3AF",
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                    No hay cursos seleccionados
                  </Typography>
                </Box>
              ) : (
                cursosSeleccionados.map((curso) => (
                  <Box
                    key={curso.code}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                      p: 1,
                      backgroundColor: "white",
                      borderRadius: 1.5,
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Chip
                        label={curso.code}
                        size="small"
                        sx={{
                          backgroundColor: "#10B981",
                          color: "white",
                          fontWeight: "bold",
                          mr: 0.8,
                          fontSize: "0.7rem",
                          height: 20,
                        }}
                      />
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {curso.name}
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={curso.chatEnabled}
                            onChange={() => handleToggleChat(curso.code)}
                            size="small"
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "#10B981",
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                {
                                  backgroundColor: "#10B981",
                                },
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.3,
                            }}
                          >
                            <Chat
                              sx={{
                                fontSize: 14,
                                color: curso.chatEnabled
                                  ? "#10B981"
                                  : "#9CA3AF",
                              }}
                            />
                            <Typography
                              variant="caption"
                              color={
                                curso.chatEnabled ? "#10B981" : "textSecondary"
                              }
                              sx={{ fontSize: "0.7rem" }}
                            >
                              Chat
                            </Typography>
                          </Box>
                        }
                        labelPlacement="start"
                        sx={{ m: 0 }}
                      />
                    </Box>
                  </Box>
                ))
              )}
            </Box>

            <Box
              sx={{ mt: 1.5, display: "flex", gap: 1.5, alignItems: "center" }}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<Add sx={{ fontSize: 14 }} />}
                onClick={handleOpenCursosDialog}
                sx={{
                  backgroundColor: "#10B981",
                  "&:hover": { backgroundColor: "#059669" },
                  borderRadius: 1.5,
                  fontSize: "0.75rem",
                }}
              >
                Gestionar Cursos
              </Button>
              {cursosSeleccionados.length > 0 && (
                <Typography
                  variant="caption"
                  color="#10B981"
                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                >
                  {cursosSeleccionados.length} curso(s) seleccionado(s) •{" "}
                  {cursosSeleccionados.filter((c) => c.chatEnabled).length} con
                  chat
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Número de meses y Cantidad de seriales */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <NumericInput 
                label={"Número de meses:"} 
                placeholder={"Ingrese número de meses"}
                sufixText={"meses"}
                helperText="Mínimo 1 mes, máximo 999 meses"
                value={numeroMeses}
                min={1}
                max={999}
                setter={setNumeroMeses}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <NumericInput 
                label={"Número de activaciones:"} 
                placeholder={"Ingrese número de activaciones"}
                sufixText={"activaciones"}
                helperText="Mínimo 1 activación, máximo 999 activaciones"
                value={numeroActivaciones}
                min={1}
                max={999}
                setter={setNumeroActivaciones}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              
              <NumericInput 
                label={"Cantidad de seriales:"} 
                placeholder={"Ingrese cantidad de seriales"}
                sufixText={"seriales"}
                helperText="Mínimo 1 serial, máximo 9,999 seriales"
                value={cantidadSeriales}
                min={1}
                max={9999}
                setter={setCantidadSeriales}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Observaciones */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <Notes sx={{ color: "#6B7280", fontSize: 16 }} />
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ fontSize: "0.85rem" }}
              >
                Observaciones:
              </Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              variant="outlined"
              placeholder="Ingrese observaciones sobre este lote de seriales..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#B0BEC5",
                  color: "white",
                  fontSize: "0.8rem",
                  "& input::placeholder": {
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                  "& textarea::placeholder": {
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                },
              }}
            />
          </Box>

          {/* Botón Generar */}
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Button
              variant="contained"
              size="medium"
              onClick={handleGenerar}
              disabled={!isFormValid()}
              sx={{
                backgroundColor: "#9CA3AF",
                color: "white",
                fontWeight: "bold",
                px: 3,
                py: 1,
                borderRadius: 2,
                fontSize: "0.8rem",
                "&:hover": {
                  backgroundColor: "#6B7280",
                },
                "&:disabled": {
                  backgroundColor: "#D1D5DB",
                  color: "#9CA3AF",
                },
              }}
            >
              Generar
            </Button>
          </Box>

          {/* Resumen */}
          {(cursosSeleccionados.length > 0 || distribuidorSeleccionado) && (
            <Card
              sx={{
                mt: 2,
                backgroundColor: "#F0F9FF",
                border: "1px solid #BAE6FD",
              }}
            >
              <CardContent sx={{ p: 1.5 }}>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="#0369A1"
                  gutterBottom
                  sx={{ fontSize: "0.75rem" }}
                >
                  Resumen del lote:
                </Typography>
                <Typography
                  variant="caption"
                  color="#0369A1"
                  sx={{ fontSize: "0.7rem", display: "block" }}
                >
                  • Distribuidor:{" "}
                  {distribuidorSeleccionado?.name || "Sin especificar"}
                  {distribuidorSeleccionado && (
                    <Typography
                      component="span"
                      sx={{ fontSize: "0.65rem", opacity: 0.8 }}
                    >
                      {" "}
                      (ID: {distribuidorSeleccionado.id})
                    </Typography>
                  )}
                </Typography>
                <Typography
                  variant="caption"
                  color="#0369A1"
                  sx={{ fontSize: "0.7rem", display: "block" }}
                >
                  • Cursos: {cursosSeleccionados.length} seleccionados
                </Typography>
                <Typography
                  variant="caption"
                  color="#0369A1"
                  sx={{ fontSize: "0.7rem", display: "block" }}
                >
                  • Cursos con chat:{" "}
                  {cursosSeleccionados.filter((c) => c.chatEnabled).length}
                </Typography>
                <Typography
                  variant="caption"
                  color="#0369A1"
                  sx={{ fontSize: "0.7rem", display: "block" }}
                >
                  • Duración: {numeroMeses || "0"} meses
                </Typography>
                <Typography
                  variant="caption"
                  color="#0369A1"
                  sx={{ fontSize: "0.7rem", display: "block" }}
                >
                  • Cantidad: {cantidadSeriales || "0"} seriales
                </Typography>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
      <CourseManagerDialog
        open={openCursosDialog}
        onClose={handleCloseCursosDialog}
        onSave={handleSaveCursos}
        selectedCourses={tempCursosSeleccionados}
        availableCourses={cursosDisponiblesParaAgregar}
        onToggleChatAll={handleToggleChatMasivo}
        onToggleChat={handleToggleChatInModal}
        onRemoveCourse={handleRemoveCurso}
        onAddCourse={handleAddCurso}
      />
    </Box>
  );
}
